/**
 * The ONLY place this app talks to `/api/claims`.
 *
 * There is no TypeScript here to catch a response-shape change, so a single
 * access layer is the whole of the containment: when the backend's declared
 * resource moves, one file moves with it rather than a dozen views.
 *
 * ⚠️ `$http` (normalAxios, baseURL `…/api/`), NOT `$axios` — that one is
 * `…/api/admin/` and the claim line is not under it. The legacy injured-parcel
 * screens use `$http` for the same reason.
 *
 * ── two rules the backend's contract asks of every caller ────────────────────
 *
 * MONEY IS A STRING. `"450000.00"`. Format it for display, send back what the
 * user typed, and never compare two of them in JavaScript to decide anything —
 * whether a claim may close is a server decision published in
 * `claim.permissions.resolve`. (The backend is decimal end to end because a
 * float comparison once made a fully-paid claim refuse to close.)
 *
 * PERMISSIONS ARE A HINT. Every value in `claim.permissions` is produced by the
 * same predicate the guard uses, so they cannot disagree — but a role revoked
 * mid-session, or a claim that moved between the fetch and the click, still
 * produces a 403. `handleActionError` reloads the claim rather than trusting
 * what we were told a moment ago.
 */
import client from 'axios';

/**
 * The claim line's own axios instance.
 *
 * Built from the package rather than reusing `$http` for one reason: the claim
 * endpoints sit behind `Localization`, which reads the **`Locale` header** and
 * accepts only `en`, `lo`, `zh`. This app stores its language as `Lang=LA|CN`
 * and sends it as a `lang` QUERY PARAM, which that middleware ignores — so every
 * claim call was answered in English while the screen was in Lao. Adding the
 * header to the shared instance would change every request in the app, so the
 * claim line carries its own.
 *
 * ⚠️ `la` is NOT accepted on the wire even though it is the app's own code for
 * Lao — the backend maps `lo` onto its internal `la` AFTER the check, so sending
 * `la` is a hard 403. `LA` maps to `lo` here for exactly that reason.
 */
// TH maps to 'en' until the API's Localization middleware lists 'th' (unknown locales 403).
const LOCALE_BY_APP_LANG = { LA: 'lo', CN: 'zh', EN: 'en', TH: 'th' };

const claimHttp = client.create({ baseURL: process.env.VUE_APP_BASE_API_URL_HTTP });

claimHttp.interceptors.request.use((config) => {
    const token = window.localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }

    config.headers.Locale = LOCALE_BY_APP_LANG[window.localStorage.getItem('Lang')] || 'lo';
    return config;
});

export default {
    data: () => ({
        server_errors: {},

        /**
         * The last refusal, kept ON THE SCREEN rather than in a toast that has
         * already gone by the time an officer reads it.
         *
         * `{ message, code }`. The code is the ERP's own machine-readable one
         * (`BUDGET_EXCEEDED` / `VALIDATION_FAILED` / `INVALID_STATE`), which the
         * backend publishes as a separate `error_code` field as of
         * `fix-claim-reads-blocking-frontends`. Read THAT, never the envelope's
         * `code` — that one is the HTTP status, and branching on it would fire
         * this panel on every error in the application.
         */
        lastActionError: null,
    }),

    methods: {
        // ── reads ───────────────────────────────────────────────────────────

        claimList(params = {}) {
            return claimHttp.get('claims', { params });
        },

        claimDetail(id) {
            return claimHttp.get(`claims/${id}`);
        },

        claimShipmentLookup(shipmentNumber) {
            return claimHttp.get(`claims/shipments/${shipmentNumber}`);
        },

        claimErpDocument(id) {
            return claimHttp.get(`claims/${id}/erp`);
        },

        /**
         * The files held on the claim's ERP document — the evidence WE sent up, read back from the
         * ERP. Staff only.
         *
         * ⚠️ Not the transfer slip. Finance attaches that to the payment, which the ERP keeps
         * behind a people-only route our API key cannot reach. What we know about the money moving
         * is the settlement's type, reference and date.
         */
        claimErpFiles(id) {
            return claimHttp.get(`claims/${id}/erp/files`);
        },

        /**
         * A short-lived presigned link to one ERP file, fetched fresh per click —
         * never cached (the link is the ERP's own, with a short TTL).
         */
        claimErpFileDownload(id, attId) {
            return claimHttp.get(`claims/${id}/erp/files/${attId}/download`);
        },

        /**
         * The branches a claim can be assigned to.
         *
         * The claim line's own, not `finance/list-branches` — that one wants
         * accounting roles and `admin/list-branches` wants SuperAdmin, so a
         * claim officer is refused by both and the assess screen's branch picker
         * came up empty on a field that is not optional.
         */
        claimBranches() {
            return claimHttp.get('claims/branches');
        },

        /**
         * The sorting centres a claim can be assigned to — the OTHER register.
         *
         * Its own endpoint because it is its own table, not a filter of the branch list. The claim
         * line believed for a long time that a sorting centre was a `branches` row of a particular
         * type; it is not, and the fifteen real centres appear in that table not at all. While this
         * screen offered one list, an assessor could not name a single one of them.
         */
        claimSortingCentres() {
            return claimHttp.get('claims/sorting-centres');
        },

        /**
         * The branches on THIS claim's parcel timeline — its origin, its
         * destination, and every branch it arrived at. The assess picker's
         * default, so an officer chooses from the few branches the parcel
         * actually passed through rather than all ~811. Returns an empty list
         * when the path is unrecorded — the caller's cue to fall back to
         * `claimBranches()`.
         */
        claimTimelineBranches(id, itemId = null) {
            return claimHttp.get(`claims/${id}/branches`, {
                // The branches THIS parcel passed through. A claim of three boxes has three paths.
                params: itemId ? { claim_item_id: itemId } : {},
            });
        },

        claimReportSummary(params = {}) {
            return claimHttp.get('claims/reports/summary', { params });
        },

        /**
         * The report window as a file.
         *
         * `responseType: 'blob'` because the answer is binary — without it axios decodes the
         * .xlsx as UTF-8 text and the file that lands on disk is corrupt. The same reason
         * `claimAttachment` sets it.
         */
        claimReportExport(params = {}) {
            return claimHttp.get('claims/reports/export', { params, responseType: 'blob' });
        },

        claimReportRows(params = {}) {
            return claimHttp.get('claims/reports/rows', { params });
        },

        // ── case work ───────────────────────────────────────────────────────
        // One action per call. Deliberately NOT a status dropdown driving a
        // single endpoint — that is the legacy shape this line replaces.

        claimAccept(id) {
            return claimHttp.post(`claims/${id}/accept`);
        },

        claimContact(id, note) {
            return claimHttp.post(`claims/${id}/contact`, { note });
        },

        claimRequestInfo(id, infoRequest) {
            return claimHttp.post(`claims/${id}/request-info`, { info_request: infoRequest });
        },

        claimReject(id, reason) {
            return claimHttp.post(`claims/${id}/reject`, { reason });
        },

        /**
         * ⚠️ The claim-level assess, for a claim of exactly ONE parcel.
         *
         * Assessment is a decision about a box, and it moved to the parcel with
         * `file-one-claim-for-many-parcels`. A claim of several is refused here rather than having
         * one of its parcels picked for it — use `claimAssessParcel`.
         */
        claimAssess(id, payload) {
            return claimHttp.post(`claims/${id}/assess`, payload);
        },

        // ── the parcels ─────────────────────────────────────────────────────
        // A claim is a conversation with a claimant; each parcel in it is a case of its own,
        // assessed on its own evidence and approved or refused on its own merits.

        /**
         * One more parcel on a case that is already open — the claimant who telephones next week
         * about another box, rather than starting a second claim. `multipart/form-data`: the
         * parcel carries its own photographs.
         */
        claimAddParcel(id, formData) {
            return claimHttp.post(`claims/${id}/items`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },

        claimAssessParcel(id, itemId, payload) {
            return claimHttp.post(`claims/${id}/items/${itemId}/assess`, payload);
        },

        /** Refuse ONE parcel. The others are untouched — a claim is not a verdict. */
        claimRejectParcel(id, itemId, reason) {
            return claimHttp.post(`claims/${id}/items/${itemId}/reject`, { reason });
        },

        /**
         * Close one parcel, on the settlement of ITS OWN document. A sibling's document being paid
         * says nothing about this box.
         */
        claimResolveParcel(id, itemId, remark) {
            return claimHttp.post(`claims/${id}/items/${itemId}/resolve`, { remark });
        },

        claimResolve(id, remark) {
            return claimHttp.post(`claims/${id}/resolve`, { remark });
        },

        // ── the ERP document ────────────────────────────────────────────────
        // There is no `approve` here and there never will be: approval is a
        // person's, in the ERP, and reaches us through the poller.

        claimErpSubmit(id, payee) {
            return claimHttp.post(`claims/${id}/erp/submit`, payee);
        },

        /**
         * Evidence on an existing claim — `multipart/form-data`, and `images[]`
         * is the field name the API expects whatever the file actually is.
         *
         * ⚠️ Compress to `maxFileBytes` BEFORE calling. nginx cuts in below
         * PHP's own limit and answers an oversized upload with a bare HTML 413
         * that no interceptor here can turn into a message — the upload simply
         * appears to do nothing.
         */
        claimUploadAttachment(id, kind, files, itemId = null) {
            const form = new FormData();
            form.append('kind', kind);
            // WHICH parcel this file is evidence for. Omitted, it lands on the claim's only parcel
            // — or, on a claim of several, on the case rather than being guessed onto a box: a
            // photograph attributed to the wrong bill travels to the wrong ERP document.
            if (itemId) form.append('claim_item_id', itemId);
            Array.from(files).forEach((file) => form.append('images[]', file));

            return claimHttp.post(`claims/${id}/attachments`, form, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },

        /**
         * The accounts this CLAIMANT's compensation may be paid into.
         *
         * Default first — the COD account when they have one, because a
         * claimant already receiving COD money almost always wants their
         * compensation in the same place. `source` tells the two apart: `cod`
         * is read from the customer record and is not a row anyone here may
         * edit, `saved` is one they (or staff) added.
         */
        getClaimPayoutAccounts(id) {
            return claimHttp.get(`claims/${id}/payout-accounts`);
        },

        /**
         * Records an account for a claimant the officer is on the telephone to.
         *
         * `contact_number` is what ties it to them — staff may record for
         * someone else, a customer never may, and the server enforces that
         * rather than trusting this call.
         */
        createClaimPayoutAccount(payload) {
            return claimHttp.post('claims/payout-accounts', payload);
        },

        /**
         * ⚠️ The claim-level ERP cancel, for a claim with exactly ONE document.
         *
         * A claim raises one document per liable party per submission, so a claim of several is
         * refused here — cancelling the wrong one releases the wrong budget and hands the wrong
         * parcels back, and neither is visible from the response.
         */
        claimErpCancel(id, reason) {
            return claimHttp.post(`claims/${id}/erp/cancel`, { reason });
        },

        /** Withdraw ONE document: its budget is released and its parcels come back to assessment. */
        claimErpCancelDocument(id, documentId, reason) {
            return claimHttp.post(`claims/${id}/erp/documents/${documentId}/cancel`, { reason });
        },

        /** The files on ONE of the claim's ERP documents. */
        claimErpDocumentFiles(id, documentId) {
            return claimHttp.get(`claims/${id}/erp/documents/${documentId}/files`);
        },

        claimErpDocumentFileDownload(id, documentId, attId) {
            return claimHttp.get(`claims/${id}/erp/documents/${documentId}/files/${attId}/download`);
        },

        // ── intake on behalf ────────────────────────────────────────────────

        /**
         * The channel is derived from the token and is NEVER sent — a payload
         * that could claim to be a customer would be a payload that could lie.
         */
        claimCreate(formData) {
            return claimHttp.post('claims', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },

        claimAddAttachments(id, formData) {
            return claimHttp.post(`claims/${id}/attachments`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
        },

        /**
         * One file on a claim, as a blob.
         *
         * The endpoint sits behind `Authorization`, so a bare `<img src>` (which
         * sends no header) would silently fail. The viewer fetches the bytes and
         * renders them from an object URL instead.
         */
        claimAttachment(id, attId) {
            return claimHttp.get(`claims/${id}/attachments/${attId}`, { responseType: 'blob' });
        },

        // ── shared handling ─────────────────────────────────────────────────

        /**
         * What to do when an action fails.
         *
         * 403 is not an error to show and forget: it means what we were told the
         * user could do is no longer true, so the claim is reloaded and the
         * buttons redraw from the server's answer.
         *
         * The ERP's own refusals arrive as `code` + `message`, and `message` is
         * an ARRAY for `VALIDATION_FAILED` and a string otherwise — handling only
         * one shape is how an officer ends up reading `[object Object]`.
         */
        handleActionError(error, onReload) {
            const status = this.nullsafeStatus(error);
            const body = (error && error.response && error.response.data) || {};

            // 413 never reaches PHP: nginx refuses the request itself and answers
            // with 192 bytes of HTML, so there is no message to read and every
            // generic handler below would render "something went wrong". Verified
            // locally against the real server — `client_max_body_size` is unset,
            // so the ceiling is nginx's 1 MB default.
            if (status === 413) {
                const tooLarge = 'ໄຟລ໌ໃຫຍ່ເກີນທີ່ເຊີບເວີຮັບໄດ້ — ກະລຸນາຖ່າຍຮູບໃໝ່ ຫຼື ເລືອກຮູບທີ່ນ້ອຍກວ່າ';
                this.lastActionError = { message: tooLarge, code: null };
                this.$notification.error({ message: tooLarge });
                return;
            }

            // A 422 is the officer's own form telling them what to fix, and it
            // renders beside the field. Anything else is the claim refusing —
            // often the ERP — and that one has to stay put and be readable.
            //
            // `body.error_code` is present ONLY on an ERP refusal. Its absence
            // is what keeps the ERP panel off every other error.
            this.lastActionError = status !== 422
                ? { message: this.claimMessage(body), code: body.error_code || null }
                : null;

            if (status === 403 && typeof onReload === 'function') {
                this.$notification.warning({ message: this.claimMessage(body) || 'ສິດຂອງທ່ານປ່ຽນໄປ ກຳລັງໂຫຼດຄຳຮ້ອງໃໝ່' });
                onReload();
                return;
            }

            if (status === 422) {
                this.server_errors = body.errors || {};
            }

            this.$notification.error({ message: this.claimMessage(body) || 'ເກີດຂໍ້ຜິດພາດ' });
        },

        /** The ERP sends an array for one error code and a string for the rest. */
        claimMessage(body) {
            const message = body && (body.message || body.error);

            if (Array.isArray(message)) {
                return message.join(' · ');
            }

            return typeof message === 'string' ? message : null;
        },

        nullsafeStatus(error) {
            return (error && error.response && error.response.status) || 0;
        },

        /** Display only. Never parse these back to compare two of them. */
        formatMoney(value) {
            if (value === null || value === undefined || value === '') {
                return '-';
            }

            return Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 });
        },
    },
};
